﻿import MessageIcon from "@mui/icons-material/Message";
import ShareIcon from "@mui/icons-material/Share";
import { Theme } from "@mui/material";
import Avatar from "@mui/material/Avatar";
import Button from "@mui/material/Button";
import Card from "@mui/material/Card";
import CardActions from "@mui/material/CardActions";
import CardContent from "@mui/material/CardContent";
import CardMedia from "@mui/material/CardMedia";
import Chip from "@mui/material/Chip";
import Link from "@mui/material/Link";
import { styled } from "@mui/material/styles";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import useMediaQuery from "@mui/material/useMediaQuery";
import { Hit } from "_shared/_api/search/search-results";
import Guid from "_shared/guids/guid";
import { MarketplaceResultType } from "config";
import { MarketplaceSearchInfoJSON } from "marketplace/_schema/marketplace-search-response-schema";
import CompanyBookmarkButton from "marketplace/bookmarks/_views/company-bookmark-button";
import {
  getCompanyAvatar,
} from "marketplace/companies/_store/companies-redux-slice";
import { companiesApi } from "marketplace/companies/_api/company-api";
import MarketplaceCompanyInfoSchema, { MarketplaceCompanyInfoJSON } from "marketplace/_schema/marketplace-company-info-schema";
import ViewCompanyProfile from "marketplace/companies/profiles/_views/view-company-profile";
import ViewIndividualProfile from "marketplace/individuals/profiles/_views/individual-profile-container";
import ChatWindow from "messaging/forums/_views/components/forum-chat-window";
import Forum from "messaging/forums/forum";
import { enqueueSnackbar } from "notistack";
import React from "react";
import { useSelector } from "react-redux";
import { selectIsNetworkedWith } from "users/networking/connections/_selectors/user-network-connection-selectors";
import NetworkButton from "users/networking/connections/_views/components/network-button";

const ResultCard = styled(Card)(({ theme }) => ({
  [theme.breakpoints.down("xl")]: {
    display: "block",
  },
  display: "flex",
  flex: "1 0 auto",
  justifyContent: "space-between",
  marginBottom: theme.spacing(2),
  marginTop: theme.spacing(2),
  "&:first-of-type": {
    marginTop: 0,
  },
}));
const ResultHeader = styled("div")(({ theme }) => ({
  alignItems: "center",
  display: "grid",
  columnGap: theme.spacing(2),
  gridTemplateColumns: "auto 1fr",
}));
const ResultCardContent = styled(CardContent)(({ theme }) => ({
  flexGrow: "inherit",
  padding: theme.spacing(2),
  paddingLeft: theme.spacing(3),
}));
const ResultCardActions = styled(CardActions)(({ theme }) => ({
  [theme.breakpoints.down("xl")]: {
    alignContent: "flex-end",
    alignItems: "unset",
    flexDirection: "row",
    justifyContent: "flex-end",
    marginBottom: theme.spacing(2),
  },
  [theme.breakpoints.down("lg")]: {
    alignContent: "flex-start",
    alignItems: "unset",
    flexDirection: "row",
    justifyContent: "flex-start",
    marginBottom: theme.spacing(2),
  },
  alignContent: "center",
  alignItems: "flex-end",
  flex: 0,
  flexDirection: "column",
  flexWrap: "wrap",
  justifyContent: "space-evenly",
  paddingRight: theme.spacing(3),
}));
const ResultName = styled(Typography)(() => ({
  cursor: "pointer",
  textDecoration: "underline",
  "& em": {
    backgroundColor: "rgba(255, 255, 0, 0.25)",
    fontStyle: "normal",
  },
}));
const TypeChip = styled(Chip)(({ theme }) => ({
  borderColor: "#CA0",
  borderStyle: "dashed",
  color: "#CA0",
  marginRight: theme.spacing(1),
  marginTop: theme.spacing(0.5),
  opacity: 0.66,
}));
const StyledButton = styled(Button)(({ theme }) => ({
  [theme.breakpoints.down("sm")]: {
    width: "100%",
  },
  marginBottom: theme.spacing(1),
  marginLeft: theme.spacing(2),
  minWidth: theme.spacing(30),
}));
const StyledNetworkButton = styled(NetworkButton)(({ theme }) => ({
  [theme.breakpoints.down("sm")]: {
    width: "100%",
  },
  marginBottom: theme.spacing(1),
  marginLeft: theme.spacing(2),
  minWidth: theme.spacing(30),
}));
const StyledBookmarkButton = styled(CompanyBookmarkButton)(({ theme }) => ({
  [theme.breakpoints.down("sm")]: {
    width: "100%",
  },
  marginBottom: theme.spacing(1),
  marginLeft: theme.spacing(2),
  minWidth: theme.spacing(30),
}));
const Subtitle = styled(Typography)(({ theme }) => ({
  fontWeight: theme.typography.fontWeightMedium,
}));
const ChatCardContent = styled(CardContent)(({ theme }) => ({
  [theme.breakpoints.down("md")]: {
    paddingLeft: 0,
    paddingRight: 0,
  },
  width: "100%",
}));
const Details = styled("div")(({ theme }) => ({
  [theme.breakpoints.up(1920)]: {
    columnCount: 2,
  },
  [theme.breakpoints.down(1920)]: {
    columnCount: 1,
  },
  [theme.breakpoints.down("xl")]: {
    columnCount: 2,
  },
  [theme.breakpoints.down("lg")]: {
    columnCount: 2,
  },
  [theme.breakpoints.down("md")]: {
    columnCount: 1,
  },
  columnGap: theme.spacing(2),
  columnFill: "balance",
  marginTop: theme.spacing(2),
  "& em": {
    backgroundColor: "rgba(255, 255, 0, 0.25)",
    fontStyle: "normal",
  },
}));
const StyledLink = styled(Link)(({ theme }) => ({
  color: theme.palette.common.black,
  cursor: "pointer",
  textDecoration: "underline",
})) as typeof Link;
const SmallAvatar = styled(Avatar)(({ theme }) => ({
  cursor: "pointer",
  height: theme.spacing(10),
  width: theme.spacing(10),
}));
const ResultAvatarCardMedia = styled(CardMedia)(() => ({
  cursor: "pointer",
  height: "auto",
  width: "15vw",
})) as typeof CardMedia;
const ResultAvatar = styled(Avatar)(() => ({
  cursor: "pointer",
  height: "auto",
  width: "15vw",
}));
const GridRow = styled("div")(({ theme }) => ({
  display: "grid",
  gridTemplateColumns: "1fr 1fr",
  marginBottom: theme.spacing(1),
}));

export interface CompanyResultProps {
  hit: Hit<MarketplaceSearchInfoJSON>;
  result: MarketplaceCompanyInfoJSON;
  getText: (
    result: Hit<MarketplaceSearchInfoJSON>,
    fieldNames: string[],
    base?: unknown,
    noCommas?: boolean,
  ) => { __html: string | TrustedHTML };
  handleResultClicked: (
    event: React.MouseEvent,
    profileId: Guid,
    profileType: MarketplaceResultType,
    component: React.JSX.Element,
    fullWidth: boolean,
  ) => void;
}

export default function CompanyResult(props: Readonly<CompanyResultProps>) {
  const { hit, result, getText, handleResultClicked } = props;

  const companyId = result.id ? new Guid(result.id) : null;
  const companyProfile = result ? MarketplaceCompanyInfoSchema.deserializeToProfile(result) : undefined;

  // #region State
  const [showChat, setShowChat] = React.useState<boolean>(false);
  const [forum, setForum] = React.useState<Forum>();

  // #region Hooks
  const isXlDownDisplaySize = useMediaQuery((theme: Theme) => theme.breakpoints.down("xl"));
  const isXlUpDisplaySize = useMediaQuery((theme: Theme) => theme.breakpoints.up("xl"));

  // #region Selectors
  const avatar = useSelector(getCompanyAvatar(companyId));
  companiesApi.useGetCompanyAvatarQuery(
    { companyId: companyId?.value ?? "" },
    { skip: !result.id || !!avatar || !result.avatar }
  );
  const [getCompanyContactForum] = companiesApi.useGetCompanyContactForumMutation();
  const isNetworkedWithCompany = useSelector(selectIsNetworkedWith(companyProfile?.contactUserId));

  // #region Lifecycle
  // #endregion

  const contactPersonProfileLink = getProfileLink(
    MarketplaceResultType.Individual,
    ["firstName", "lastName"],
    result?.contact,
    result?.contact?.id,
    true,
  );

  async function contactCompany() {
    if (!result?.id) throw new Error("Company ID is required to contact company.");
    try {
      const forumJson = await getCompanyContactForum({
        companyId: result.id,
      }).unwrap();
      setForum(Forum.fromJSON(forumJson));
      setShowChat(true);
    } catch (error) {
      console.error(error);
    }
  }

  function getProfileLink(
    type: MarketplaceResultType,
    fields: string[],
    base: unknown,
    profileId: string | null | undefined,
    noCommas: boolean = false,
    secondary: boolean = false,
  ) {
    if (!profileId) return null;
    return (
      <StyledLink
        style={{
          fontSize: secondary ? "0.85em" : "1em",
          opacity: secondary ? 0.6 : 1,
        }}
        component={Typography}
        dangerouslySetInnerHTML={getText(hit, fields, base, noCommas)}
        onClick={(event) => {
          if (type === MarketplaceResultType.Individual) return openIndividualProfile(event, profileId);
          openCompanyProfile(event, profileId);
        }}
      />
    );
  }

  function openCompanyProfile(event: React.MouseEvent, id: string | null | undefined) {
    if (!id) return;
    const profileId = new Guid(id);
    handleResultClicked(
      event,
      profileId,
      MarketplaceResultType.Company,
      <ViewCompanyProfile companyId={profileId} />,
      true,
    );
  }

  function openIndividualProfile(event: React.MouseEvent, id: string | null | undefined) {
    if (!id) return;
    const profileId = new Guid(id);
    handleResultClicked(
      event,
      profileId,
      MarketplaceResultType.Individual,
      <ViewIndividualProfile individualId={profileId} />,
      true,
    );
  }

  return (
    <React.Fragment>
      {showChat && (
        <ResultCard raised={false} variant="outlined">
          <ChatCardContent>
            <ChatWindow forums={forum ? [forum] : []} onClose={() => setShowChat(false)} />
          </ChatCardContent>
        </ResultCard>
      )}
      {!showChat && (
        <ResultCard raised={false} variant="outlined">
          {isXlUpDisplaySize && (
            <>
              <ResultAvatarCardMedia
                style={{ display: avatar ? undefined : "none" }}
                component="img"
                image={avatar}
                onClick={(event) => openCompanyProfile(event, result?.id)}
              />
              <ResultAvatar
                style={{ display: avatar ? "none" : undefined }}
                variant="square"
                onClick={(event) => openCompanyProfile(event, result?.id)}
              />
            </>
          )}
          <ResultCardContent>
            <ResultHeader>
              {isXlDownDisplaySize && (
                <span>
                  <SmallAvatar
                    style={{ display: avatar ? undefined : "none" }}
                    variant="circular"
                    src={avatar}
                    onClick={(event) => openCompanyProfile(event, result?.id)}
                  />
                  <SmallAvatar
                    style={{ display: avatar ? "none" : undefined }}
                    variant="circular"
                    onClick={(event) => openCompanyProfile(event, result?.id)}
                  />
                </span>
              )}
              <span>
                <ResultName
                  variant="h5"
                  dangerouslySetInnerHTML={getText(hit, ["name"], result, true)}
                  onClick={(event) => openCompanyProfile(event, result?.id)}
                />
                <TypeChip label="Company" variant="outlined" size="small" />
              </span>
            </ResultHeader>
            <Details>
              {result?.description && (
                <GridRow>
                  <Subtitle variant="body1">Who We Are:</Subtitle>
                  <Typography
                    dangerouslySetInnerHTML={{
                      __html: (() => {
                        const description = getText(hit, ["description"]).__html as string;
                        return description.length > 250 ? `${description.substring(0, 250)}...` : description;
                      })(),
                    }}
                  />
                </GridRow>
              )}
              {result?.contact?.firstName && result?.contact?.lastName && (
                <GridRow>
                  <Subtitle variant="body1">Contact Person:</Subtitle>
                  {contactPersonProfileLink}
                </GridRow>
              )}
            </Details>
          </ResultCardContent>
          <ResultCardActions disableSpacing>
            {companyProfile?.contactUserId && <StyledNetworkButton userId={companyProfile.contactUserId} />}
            <StyledBookmarkButton companyId={result?.id ? new Guid(result?.id) : undefined} />
            <Tooltip title={isNetworkedWithCompany ? "" : "You must be in a company's network before contacting them"}>
              <div style={{ display: "contents", width: "100%" }}>
                <StyledButton
                  startIcon={<MessageIcon />}
                  variant="outlined"
                  disabled={!isNetworkedWithCompany}
                  onClick={contactCompany}
                >
                  Send Message
                </StyledButton>
              </div>
            </Tooltip>
            <StyledButton
              aria-label="share"
              variant="outlined"
              startIcon={<ShareIcon />}
              onClick={() => {
                navigator.clipboard
                  .writeText(`${globalThis.location.origin}/marketplace/company/${result?.id}`)
                  .then(() => {
                    enqueueSnackbar("Link copied to clipboard", {
                      variant: "info",
                    });
                  })
                  .catch(() => {
                    enqueueSnackbar("Couldn't copy link to clipboard. Please try again", {
                      variant: "error",
                    });
                  });
              }}
            >
              Share
            </StyledButton>
          </ResultCardActions>
        </ResultCard>
      )}
    </React.Fragment>
  );
}
